import { defineStore } from 'pinia'
import { factory } from './../../../../factory-api/factory'
import { sortList, getRequestParameters } from '@/application/helpers/documentsHelper'
import {
  type DeleteFileRequest,
  type DocumentResponse,
  type DocumentTypeItem,
  type FileResponseBase64,
  type ResetPublicRequest,
  type SetPublicRequest,
  type UpdateFileDataRequest,
  type UploadDocumentsRequest,
  type Document,
  UploadTypes
} from '../../../../generated-api'
import { useFileStore, useLocalizationStore } from '@/stores'
import { convertFileToBase64, base64ToBlob } from '@/application/helpers'
import { FilterItemType, type IFilterItem } from '@/interfaces/IFilterItem'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'
import type { IFileUploadRequest } from '@/src/interfaces/IFileUploadRequest'

export const useDocumentsStore = defineStore('documents', () => {
  const localizationStore = useLocalizationStore()
  const fileStore = useFileStore()

  const currentDocuments = ref<Document[]>([])
  const otherDocuments = ref<Document[]>([])
  const filters = ref<IFilterItem[]>([])

  const isInitializing = ref<boolean>(true)
  const openImagePreviewDialog = ref<boolean>(false)

  const publicDocument = ref<Document | undefined>()
  const publicDocumentId = ref<string>('')
  const searchString = ref<string>('')
  const documentAsBase64 = ref<string>('')
  const currentDocumentExtension = ref<string>('')
  const editingId = ref<string>('')
  const addCvText = ref<string>('')
  const seeImagePreviewText = ref<string>('')

  const isUploadingFile = ref<boolean>(false)

  const documentResponse = ref<DocumentResponse>()

  const initializeDocumentSection = async () => {
    await getPublicDocument()
    await updateDocuments()
    await setFilters()
  }

  // get documents
  const getDocumentList = async (newDocumentList?: DocumentResponse) => {
    if (newDocumentList === undefined) {
      const documentApi = await factory.getDocumentApi()
      documentResponse.value = await documentApi.list()
    } else {
      documentResponse.value = newDocumentList
    }
  }

  const getDocumentsByType = async (type: string) => {
    const documentApi = await factory.getDocumentApi()
    const response = await documentApi.getDocumentsByType({ type: type })
    currentDocuments.value = response
  }

  const documentTypes = (): SelectItem[] | undefined => {
    const list: SelectItem[] = []
    documentResponse.value?.settings?.documentType?.forEach((type: DocumentTypeItem) => {
      list.push({
        name: type.value!,
        id: type.key
      })
    })
    return list
  }

  const updateDocuments = async (newDocumentList?: DocumentResponse, type?: string) => {
    if (type === undefined) {
      await getDocumentList(newDocumentList)
    } else {
      await getDocumentsByType(type)
    }

    if (documentResponse.value != undefined) {
      currentDocuments.value = documentResponse.value.current ?? []
      otherDocuments.value = documentResponse.value.others ?? []
    }
  }

  const setFilters = async () => {
    const documentType = documentResponse.value?.settings?.documentType

    if (documentType === undefined) {
      return
    }

    filters.value = [
      {
        id: 'documentTypes',
        name: localizationStore.translate('general_Choose_Type'),
        options: documentType.map((item: DocumentTypeItem) => {
          return {
            value: item.key,
            name: item.value
          }
        }),
        selected: [],
        hide: false,
        type: FilterItemType.Checkbox
      },
      {
        id: 'documentSort',
        name: localizationStore.translate('general_Choose_Sorting'),
        options: [
          { name: localizationStore.translate('general_Title_A_Z'), value: 'title' },
          { name: localizationStore.translate('general_Date_Newest_First'), value: 'date' },
          { name: localizationStore.translate('general_Type_A_Z'), value: 'type' }
        ],
        selected: [],
        hide: false,
        type: FilterItemType.Select
      }
    ] as IFilterItem[]
  }

  const filteredLists = () => {
    if (
      currentDocuments &&
      filters.value[0] &&
      filters.value[0].selected.length <= 0 &&
      filters.value[1] &&
      filters.value[1]?.selected[0] === undefined &&
      searchString.value === ''
    ) {
      return {
        current: currentDocuments.value,
        other: otherDocuments.value
      }
    } else {
      return {
        current: filterList(currentDocuments.value),
        other: filterList(otherDocuments.value)
      }
    }
  }

  const filterList = (list: Document[] | undefined) => {
    if (list === undefined) {
      return []
    }

    let filteredList = list.slice()

    if (filters.value[1]?.selected[0] !== undefined) {
      sortList(filteredList, filters.value[1].selected[0])
    }

    if (filters.value[0].selected.length) {
      filteredList = filteredList.filter((document) => {
        return document?.type !== undefined && filters.value[0].selected.includes(document.type)
      })
    }

    if (searchString.value !== '') {
      filteredList = filteredList.filter((document) =>
        document.title?.toLocaleLowerCase().includes(searchString.value.toLocaleLowerCase())
      )
    }

    return filteredList
  }

  const setSearchString = (value: string) => {
    searchString.value = value
  }

  const downloadDocument = async (programmeId: string, documentId: string) => {
    const requestParameters = getRequestParameters(programmeId, documentId)
    const documentApi = await factory.getDocumentApi()
    const response = await documentApi.downloadFileAsBase64(requestParameters)

    if (
      response !== undefined &&
      response.fileContentBase64 !== undefined &&
      response.fileName !== undefined
    ) {
      fileStore.downloadFile(await base64ToBlob(response.fileContentBase64), response.fileName)
    }
  }

  const deleteDocument = async (id: string) => {
    const requestParameters: DeleteFileRequest = {
      fileId: id
    }

    // Reset public document when id of document selected to be deleted is public document
    if (id === publicDocumentId.value) {
      await resetPublicDocument()
    }

    currentDocuments.value.splice(findDocumentIndex(id), 1)
    const documentApi = await factory.getDocumentApi()
    await documentApi.deleteFile(requestParameters)
  }

  const uploadDocuments = async (
    type: UploadTypes = UploadTypes.File,
    callUpdateDocuments: boolean = true
  ) => {
    // Iterate each selectedfile and upload them to the server
    for (const requestedFile of fileStore.selectedFiles) {
      const requestParameters: UploadDocumentsRequest = {
        contentType: requestedFile.file.type,
        body: await convertFileToBase64(requestedFile.file),
        fileName: requestedFile.file.name,
        documentType: requestedFile.selectItem?.id?.toString() ?? '',
        uploadTypes: type
      }
      const documentApi = await factory.getDocumentApi()
      await documentApi.uploadDocuments(requestParameters)
    }

    fileStore.resetFileStore()
    if (callUpdateDocuments === true) {
      await updateDocuments(undefined, type)
    }
  }

  const updateDocument = async (documentId: string, documentName: string, documentType: string) => {
    const document = findDocument(documentId)

    if (document === undefined) {
      return
    }

    document.title = documentName
    document.type = documentType

    const requestParameters: UpdateFileDataRequest = {
      body: {
        id: document.id,
        name: documentName,
        type: documentType
      }
    }
    const documentApi = await factory.getDocumentApi()
    const documentResponse = await documentApi.updateFileData(requestParameters)

    await updateDocuments(documentResponse)
  }

  const getPublicDocument = async () => {
    const documentApi = await factory.getDocumentApi()
    const getPublicDocument = await documentApi.publicFile({ type: 'CV' })

    if (getPublicDocument != undefined) {
      publicDocumentId.value = getPublicDocument.id as string
      setLocalPublicDocument(getPublicDocument)
    }
  }

  const setPublicDocument = async (documentId: string, name?: string) => {
    if (publicDocumentId.value !== undefined && publicDocumentId.value != '') {
      await resetPublicDocument()
    }

    const newPublicDocument = findDocument(documentId)
    const requestParameters: SetPublicRequest = {
      documentId: documentId,
      documentType: 'CV',
      programmeId: newPublicDocument.programmeId,
      name: name
    }
    const documentApi = await factory.getDocumentApi()
    await documentApi.setPublic(requestParameters)
    publicDocumentId.value = documentId
  }

  const resetPublicDocument = async () => {
    // create a request parameter
    const requestParamters: ResetPublicRequest = {
      documentId: publicDocumentId.value,
      documentType: 'CV'
    }

    // request a reset using the requestParameters
    const documentApi = await factory.getDocumentApi()
    await documentApi.resetPublic(requestParamters)

    setLocalPublicDocument(undefined)
    publicDocumentId.value = ''
  }

  const findDocument = (id: string): Document => {
    const document: Document | undefined =
      currentDocuments.value.find((x) => x.id === id) === undefined
        ? otherDocuments.value.find((x) => x.id === id)
        : currentDocuments.value.find((x) => x.id === id)

    if (document === undefined) {
      return {} as Document
    }

    return document
  }

  const findDocumentIndex = (id: string) => {
    return currentDocuments.value.findIndex((doc: Document) => doc.id === id) === undefined
      ? otherDocuments.value.findIndex((doc: Document) => doc.id === id)
      : currentDocuments.value.findIndex((doc: Document) => doc.id === id)
  }

  const getDocumentAsBase64 = async (
    programmeId: string,
    documentId: string
  ): Promise<FileResponseBase64 | undefined> => {
    try {
      const requestParameters = getRequestParameters(programmeId, documentId)
      const documentApi = await factory.getDocumentApi()
      const response = await documentApi.downloadFileAsBase64(requestParameters)

      return response
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return undefined
    }
  }

  const openPreview = async (document: Document) => {
    documentAsBase64.value = ''

    setOpenImagePreviewDialog(true)

    const file = await getDocumentAsBase64(document.programmeId!, document.id!)
    documentAsBase64.value = file?.fileContentBase64 !== undefined ? file.fileContentBase64 : ''
  }

  const setOpenImagePreviewDialog = (toggle: boolean) => {
    openImagePreviewDialog.value = toggle
  }

  const setIsIntializing = (initialize: boolean) => {
    isInitializing.value = initialize
  }

  const resetDocumentStore = () => {
    currentDocuments.value = []
    otherDocuments.value = []
  }

  const setAddCv = (translation: string) => {
    addCvText.value = translation
  }

  const setSeeImagePreview = (translation: string) => {
    seeImagePreviewText.value = translation
  }

  const updateFilterItem = (id: string, selected: string[]) => {
    filters.value = filters.value.map((filter) =>
      filter.id === id ? { ...filter, selected } : filter
    )
  }

  const setPublicDocumentId = (id: string) => {
    publicDocumentId.value = id
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveProfileImage = async (cropper: any, type: string) => {
    const croppedCanvas = cropper?.getResult().canvas

    if (croppedCanvas != undefined) {
      const blob = await new Promise<Blob | null>((resolve) => {
        croppedCanvas.toBlob((blob: Blob) => resolve(blob), type)
      })

      if (blob != undefined) {
        const file = new File([blob], 'cropped-image.' + (type.split('/').pop() ?? '.png'), {
          type: blob.type
        })

        const fileObject: IFileUploadRequest = {
          file: file,
          isUploaded: false,
          statusMessage: 'dfg',
          selectItem: { name: '', id: '' }
        }

        await fileStore.setSelectedFile(fileObject)
        await uploadDocuments(UploadTypes.ProfileImage, false)
      }
    }
  }

  const setLocalPublicDocument = (document: Document | undefined) => {
    publicDocument.value = document
  }

  return {
    initializeDocumentSection,
    filters,
    downloadDocument,
    deleteDocument,
    uploadDocuments,
    updateDocument,
    publicDocumentId,
    getDocumentAsBase64,
    documentAsBase64,
    openPreview,
    openImagePreviewDialog,
    setOpenImagePreviewDialog,
    currentDocumentExtension,
    isInitializing,
    filteredLists,
    searchString,
    setSearchString,
    findDocument,
    findDocumentIndex,
    currentDocuments,
    setIsIntializing,
    otherDocuments,
    documentTypes,
    setPublicDocument,
    resetDocumentStore,
    editingId,
    addCvText,
    setAddCv,
    seeImagePreviewText,
    setSeeImagePreview,
    updateFilterItem,
    getDocumentsByType,
    resetPublicDocument,
    setPublicDocumentId,
    getPublicDocument,
    publicDocument,
    saveProfileImage,
    isUploadingFile,
    setLocalPublicDocument
  }
})
